import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { RotateCw, SearchCheck, ShieldX } from 'lucide-react'
import {
  Button,
  Card,
  ErrorState,
  MoneyValue,
  SelectField,
  StatusBadge,
  TextField,
} from '@shared/design-system/ui'
import { useSession } from '@shared/lib/auth/session-store'
import { hasAdminPermission } from '@shared/lib/auth/permissions'
import { toast } from '@shared/lib/toast/toast-store'
import { getUserFacingError } from '@shared/lib/errors/user-facing-error'
import {
  dismissChargebackCase,
  manualMatchChargebackCase,
  useAdminChargebackCases,
} from '../api/admin.api'
import type { ChargebackCaseItem } from '../api/admin.api'
import { TechnicalId } from './TechnicalId'
import {
  actionRow,
  clickableRow,
  detailGrid,
  detailItem,
  fieldLabel,
  header,
  muted,
  page,
  panel,
  panelSubtitle,
  panelTitle,
  subtitle,
  table,
  tableCard,
  tableViewport,
  td,
  th,
  title,
  titleBlock,
  value,
} from './admin.css'

const statusOptions = [
  { label: 'Todos', value: '' },
  { label: 'Sem correspondência', value: 'UNMATCHED' },
  { label: 'Revisão manual', value: 'MANUAL_REQUIRED' },
  { label: 'Não encontrado', value: 'NOT_FOUND' },
  { label: 'Falhou', value: 'FAILED' },
  { label: 'Prevenido', value: 'PREVENTED' },
  { label: 'Já disputado', value: 'ALREADY_DISPUTED' },
  { label: 'Descartado', value: 'DISMISSED' },
]

export function AdminChargebackCasesScreen() {
  const [status, setStatus] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [saleIdOverride, setSaleIdOverride] = useState<{ caseId: string; value: string } | null>(
    null,
  )
  const [dismissReasonDraft, setDismissReasonDraft] = useState<{
    caseId: string
    value: string
  } | null>(null)
  const currentUser = useSession((state) => state.user)
  const canManage = hasAdminPermission(currentUser, 'CHARGEBACK_MANAGE')
  const queryClient = useQueryClient()
  const cases = useAdminChargebackCases(status)
  const items = cases.data?.items ?? []
  const selected = items.find((item) => item.id === selectedId) ?? items[0] ?? null
  const saleId =
    saleIdOverride?.caseId === selected?.id
      ? (saleIdOverride?.value ?? '')
      : (selected?.sale_id ?? '')
  const dismissReason =
    dismissReasonDraft?.caseId === selected?.id ? (dismissReasonDraft?.value ?? '') : ''

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin-chargeback-cases'] })
  }
  const matchMutation = useMutation({
    mutationFn: (item: ChargebackCaseItem) =>
      manualMatchChargebackCase(item, { saleId: saleId.trim() }),
    onSuccess: () => {
      toast.success('Caso associado e processado com auditoria.')
      refresh()
    },
    onError: (error) => {
      toast.error(
        getUserFacingError(error, {
          title: 'Não foi possível associar o caso',
          fallback: 'Confira a venda informada e tente novamente.',
        }),
      )
    },
  })
  const dismissMutation = useMutation({
    mutationFn: (item: ChargebackCaseItem) =>
      dismissChargebackCase(item, { reason: dismissReason.trim() }),
    onSuccess: () => {
      toast.success('Caso descartado com justificativa.')
      refresh()
    },
    onError: (error) => {
      toast.error(
        getUserFacingError(error, {
          title: 'Não foi possível descartar o caso',
          fallback: 'Confira a justificativa e tente novamente.',
        }),
      )
    },
  })

  return (
    <section className={page} data-testid="chargebacks-screen">
      <div className={header}>
        <div className={titleBlock}>
          <h1 className={title}>Chargebacks globais</h1>
          <p className={subtitle}>
            Alertas Paylab, associação manual segura e descarte auditado de casos ambíguos.
          </p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<RotateCw size={16} />}
          onClick={() => void cases.refetch()}
        >
          Atualizar
        </Button>
      </div>

      <Card padding="md">
        <SelectField
          label="Status"
          value={status}
          options={statusOptions}
          onChange={(event) => {
            setStatus(event.currentTarget.value)
            setSelectedId(null)
            setSaleIdOverride(null)
            setDismissReasonDraft(null)
          }}
        />
      </Card>

      <Card padding="none" className={tableCard}>
        {cases.isError ? (
          <ErrorState
            title="Não foi possível carregar os casos"
            description="Tente novamente para consultar os chargebacks globais."
            onRetry={() => void cases.refetch()}
          />
        ) : (
          <div className={tableViewport}>
            <table className={table}>
              <thead>
                <tr>
                  <th className={th}>Caso</th>
                  <th className={th}>Status</th>
                  <th className={th}>Valor</th>
                  <th className={th}>Cartão</th>
                  <th className={th}>Venda</th>
                  <th className={th}>Atualizado</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr
                    key={item.id}
                    className={clickableRow}
                    onClick={() => {
                      setSelectedId(item.id)
                      setSaleIdOverride(null)
                      setDismissReasonDraft(null)
                    }}
                  >
                    <td className={td}>
                      <TechnicalId label={item.provider} value={item.provider_event_id} />
                    </td>
                    <td className={td}>
                      <StatusBadge
                        label={statusLabel(item.status)}
                        tone={statusTone(item.status)}
                        size="sm"
                      />
                    </td>
                    <td className={td}>
                      <MoneyValue amount={item.amount} currency={item.currency} size="sm" />
                    </td>
                    <td className={td}>
                      {item.card_brand ?? item.network ?? 'n/a'}{' '}
                      {item.card_last4 ? `•••• ${item.card_last4}` : ''}
                    </td>
                    <td className={td}>{item.sale_id ?? 'Não associada'}</td>
                    <td className={td}>{formatDateTime(item.updated_at)}</td>
                  </tr>
                ))}
                {!cases.isLoading && items.length === 0 ? (
                  <tr>
                    <td className={td} colSpan={6}>
                      <span className={muted}>Nenhum caso encontrado.</span>
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {selected ? (
        <Card padding="md">
          <div className={panel}>
            <h2 className={panelTitle}>Tratamento do caso</h2>
            <p className={panelSubtitle}>
              A associação pode executar a prevenção no gateway. Confirme a venda antes de agir.
            </p>
          </div>
          <div className={detailGrid}>
            <Detail label="Provedor" itemValue={selected.provider} />
            <Detail label="Evento" itemValue={selected.provider_event_id} />
            <Detail label="Autorização" itemValue={selected.authorization_code ?? 'n/a'} />
            <Detail label="Motivo" itemValue={selected.reason ?? 'n/a'} />
          </div>
          {canAct(selected.status) ? (
            <>
              <TextField
                label="ID da venda para associação manual"
                value={saleId}
                onChange={(event) =>
                  setSaleIdOverride({ caseId: selected.id, value: event.currentTarget.value })
                }
              />
              <div className={actionRow}>
                <Button
                  variant="primary"
                  size="sm"
                  leftIcon={<SearchCheck size={16} />}
                  disabled={!canManage || saleId.trim().length === 0 || matchMutation.isPending}
                  onClick={() => matchMutation.mutate(selected)}
                >
                  Associar e processar
                </Button>
              </div>
              <TextField
                label="Justificativa para descarte"
                value={dismissReason}
                maxLength={500}
                onChange={(event) =>
                  setDismissReasonDraft({ caseId: selected.id, value: event.currentTarget.value })
                }
              />
              <div className={actionRow}>
                <Button
                  variant="danger"
                  size="sm"
                  leftIcon={<ShieldX size={16} />}
                  disabled={
                    !canManage || dismissReason.trim().length < 3 || dismissMutation.isPending
                  }
                  onClick={() => dismissMutation.mutate(selected)}
                >
                  Descartar caso
                </Button>
              </div>
              {!canManage ? (
                <span className={muted}>Seu perfil não possui CHARGEBACK_MANAGE.</span>
              ) : null}
            </>
          ) : (
            <span className={muted}>Este caso está em estado terminal.</span>
          )}
        </Card>
      ) : null}
    </section>
  )
}

function Detail({ label, itemValue }: { label: string; itemValue: string }) {
  return (
    <div className={detailItem}>
      <span className={fieldLabel}>{label}</span>
      <span className={value}>{itemValue}</span>
    </div>
  )
}

function canAct(status: string) {
  return ['RECEIVED', 'UNMATCHED', 'MATCHED', 'NOT_FOUND', 'MANUAL_REQUIRED'].includes(status)
}

function statusLabel(status: string) {
  const labels: Record<string, string> = {
    RECEIVED: 'Recebido',
    UNMATCHED: 'Sem correspondência',
    MATCHED: 'Associado',
    NOT_FOUND: 'Não encontrado',
    MANUAL_REQUIRED: 'Revisão manual',
    FAILED: 'Falhou',
    PREVENTED: 'Prevenido',
    ALREADY_DISPUTED: 'Já disputado',
    DISMISSED: 'Descartado',
  }
  return labels[status] ?? status
}

function statusTone(status: string) {
  if (status === 'PREVENTED') return 'success' as const
  if (status === 'ALREADY_DISPUTED' || status === 'DISMISSED') return 'informative' as const
  if (status === 'FAILED') return 'closed' as const
  return 'awaiting' as const
}

function formatDateTime(input: string) {
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(
    new Date(input),
  )
}
